'use client';
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils';
import { useTranslation } from '../providers/UIProvider';
import { Icon } from './icons/Icon';
import { Loader } from './loaders/Loader';

const uploadInputVariants = cva(
  'relative border-2 border-dashed rounded-xl p-lg text-center transition-all duration-200 cursor-pointer',
  {
    variants: {
      state: {
        default: 'border-border-base hover:border-primary-base/50 hover:bg-primary-base/5',
        dragging: 'border-primary-base bg-primary-base/5 shadow-lg',
        error: 'border-error-text bg-error-bg/5',
        disabled: 'border-border-base bg-background-muted/50 cursor-not-allowed opacity-60',
        uploading: 'cursor-wait'
      }
    },
    defaultVariants: {
      state: 'default'
    }
  }
);

type UploadInputVariants = VariantProps<typeof uploadInputVariants>;

export interface UploadInputProps extends Omit<UploadInputVariants, 'state'> {
  /** Callback when files are selected and validated */
  onChange: (files: File[]) => void;
  /** Callback when validation error occurs */
  onError?: (error: string) => void;
  /** Accepted file types (MIME types, wildcards, or extensions) */
  acceptedTypes?: string[];
  /** Maximum file size in MB */
  maxFileSize?: number;
  /** Maximum number of files that can be selected at once */
  maxFiles?: number;
  /** Allow multiple file selection */
  multiple?: boolean;
  /** Disable the input */
  disabled?: boolean;
  /** Show uploading state (controlled by parent) */
  isUploading?: boolean;
  /** External error message */
  error?: string | null;
  /** Custom className */
  className?: string;
  /** Title text */
  title?: string;
  /** Description text */
  description?: string;
  /** Show accepted file types hint */
  showAcceptedTypes?: boolean;
  /** Show max file size hint */
  showMaxSize?: boolean;
  /** Disable drop handling (use when parent handles drops externally) */
  disableDrop?: boolean;
  /** Test ID for e2e testing */
  testId?: string;
}

export function UploadInput({
  onChange,
  onError,
  acceptedTypes = [],
  maxFileSize = 10,
  maxFiles = 1,
  multiple = false,
  disabled = false,
  isUploading = false,
  error = null,
  disableDrop = false,
  className,
  title,
  description,
  showAcceptedTypes = false,
  showMaxSize = false,
  testId
}: UploadInputProps) {
  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const t = useTranslation();

  /**
   * Validate files before calling onChange
   */
  const validateFiles = (
    files: File[]
  ): { valid: boolean; errors: string[]; validFiles: File[] } => {
    const errors: string[] = [];

    // Check file count
    if (maxFiles && files.length > maxFiles) {
      errors.push(`${t('UploadInput.errors.maxFiles')} ${maxFiles}`);
      return { valid: false, errors, validFiles: [] };
    }

    // Validate each file
    const validFiles: File[] = [];
    for (const file of files) {
      // Check file size
      if (file.size > maxFileSize * 1024 * 1024) {
        errors.push(`${file.name} ${t('UploadInput.errors.fileTooBig')} ${maxFileSize}MB`);
        continue;
      }

      // Check file type
      if (acceptedTypes.length > 0) {
        const isValid = acceptedTypes.some(type => {
          if (type.startsWith('.')) {
            return file.name.toLowerCase().endsWith(type.toLowerCase());
          }
          if (type.includes('/*')) {
            const [mainType] = type.split('/');
            return file.type.startsWith(mainType ?? '');
          }
          return file.type === type;
        });

        if (!isValid) {
          errors.push(`${file.name} ${t('UploadInput.errors.invalidType')}`);
          continue;
        }
      }

      validFiles.push(file);
    }

    return {
      valid: validFiles.length > 0,
      errors,
      validFiles
    };
  };

  /**
   * Handle files selected (common validation and callback)
   */
  const handleFilesSelected = (files: File[]) => {
    const { valid, errors, validFiles } = validateFiles(files);

    if (!valid || errors.length > 0) {
      const errorMessage = errors.join('; ');
      onError?.(errorMessage);
      return;
    }

    onChange(validFiles);
  };

  /**
   * Handle drag over
   */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !isUploading) {
      setIsDragging(true);
    }
  };

  /**
   * Handle drag leave
   */
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  /**
   * Handle drop
   */
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled || isUploading || disableDrop) return;

    const files = Array.from(e.dataTransfer.files);
    handleFilesSelected(files);
  };

  /**
   * Handle file input change
   */
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFilesSelected(files);
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    }
  };

  /**
   * Open file dialog
   */
  const openFileDialog = () => {
    if (!disabled && !isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * Determine component state
   */
  const getState = (): 'default' | 'dragging' | 'error' | 'disabled' | 'uploading' => {
    if (isUploading) return 'uploading';
    if (disabled) return 'disabled';
    if (error) return 'error';
    if (isDragging) return 'dragging';
    return 'default';
  };

  /**
   * Format accepted types for display
   */
  const getAcceptedTypesText = (): string => {
    if (!acceptedTypes.length) return '';

    const types = acceptedTypes.map(type => {
      if (type.startsWith('.')) return type.toUpperCase();
      if (type.includes('/*')) {
        const [mainType] = type.split('/');
        return mainType?.toUpperCase() ?? '';
      }
      // Map common MIME types to friendly names
      const mimeMap: Record<string, string> = {
        'application/pdf': 'PDF',
        'image/png': 'PNG',
        'image/jpeg': 'JPEG',
        'image/jpg': 'JPG',
        'image/webp': 'WEBP',
        'application/msword': 'DOC',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
        'text/plain': 'TXT'
      };
      return mimeMap[type] || type;
    });

    return types.join(', ');
  };

  const state = getState();

  return (
    <div
      className={cn(uploadInputVariants({ state }), className)}
      data-testid={testId}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={openFileDialog}
      role='button'
      tabIndex={disabled || isUploading ? -1 : 0}
      aria-label={title || t('UploadInput.defaultTitle')}
      aria-disabled={disabled || isUploading}
      onKeyDown={e => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled && !isUploading) {
          e.preventDefault();
          openFileDialog();
        }
      }}
    >
      <input
        ref={fileInputRef}
        type='file'
        multiple={multiple}
        accept={acceptedTypes.join(',')}
        onChange={handleFileInputChange}
        className='hidden'
        disabled={disabled || isUploading}
        aria-hidden='true'
      />

      {/* Content based on state */}
      {isUploading ? (
        <div className='flex flex-col items-center justify-center gap-md'>
          <Loader />
          <p className='text-sm font-medium text-foreground-base'>{t('UploadInput.uploading')}</p>
        </div>
      ) : (
        <div className='flex flex-col items-center justify-center gap-md'>
          {/* Upload icon */}
          <div
            className={cn(
              'rounded-full p-md transition-colors',
              isDragging ? 'bg-primary-base/20' : error ? 'bg-error-bg' : 'bg-primary-base/10'
            )}
          >
            <Icon
              name={error ? 'circle-xmark' : 'cloud-arrow-up'}
              size='xl'
              className={cn(error ? 'text-error-text' : 'text-primary-base')}
            />
          </div>

          {/* Title */}
          {title && <p className='text-base font-medium text-foreground-base'>{title}</p>}

          {/* Description */}
          {description && <p className='text-sm text-foreground-muted'>{description}</p>}

          {/* Hints */}
          {(showAcceptedTypes || showMaxSize) && (
            <div className='flex flex-col gap-xs text-xs text-foreground-muted'>
              {showAcceptedTypes && acceptedTypes.length > 0 && (
                <p>
                  {t('UploadInput.acceptedTypes')}: {getAcceptedTypesText()}
                </p>
              )}
              {showMaxSize && (
                <p>
                  {t('UploadInput.maxSize')}: {maxFileSize}MB
                  {maxFiles > 1 && ` • ${t('UploadInput.maxFiles')}: ${maxFiles}`}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error message */}
      {error && <p className='text-sm text-error-text mt-md font-medium'>{error}</p>}
    </div>
  );
}

UploadInput.displayName = 'UploadInput';
